// v4 (spec-v4-structure-and-stage.md 5.3, 6.5; ticket V4-C4): the cast, everywhere it shows. Moved here from
// VideoPanels.tsx (v3's CastPanel / CastCard) and reworked for character versions:
//   CastTile / CastRow   the small cards on a collection (and anywhere a row of cast fits): sheet, name, state, older looks
//   CastPanel / CastCard a video's Cast tab: each character's sheet (with live progress while one is drawn), its state
//                        and version, the voice, the price before any sheet is made, and a link to the Character screen
//   SheetConfirm         the priced "Make / Redo the sheet?" sheet (the Character screen uses it too)
//   VoicePick            pick a voice by describing it (the Character screen uses it too)
// A sheet is made through V4-C2's POST /characters/:id/sheet once the character has a row (GET /projects/:id/characters);
// before that (an older API) the v3 project route is the fallback.
import { useEffect, useState, type FormEvent } from "react";
import { tapTool } from "../actions";
import { api } from "../api";
import { toggleAudio } from "../audio";
import {
  characterPath,
  characters,
  SHEET_CREDITS,
  sheetButton,
  sheetJobs,
  sheetProgress,
  STATE_WORD,
  versionThumb,
  type CharacterInfo,
  type SheetBody,
  type SheetPart,
} from "../characters";
import type { CastMember } from "../collections";
import { director } from "../director";
import { videoBrief, type Character, type FormatInfo } from "../formats";
import { Link } from "../router";
import { onJob } from "../session";
import { bump, toast, useRefresh, useStore } from "../store";
import type { JobView, Project, VoiceInfo } from "../types";
import { fmtCredits, Icon, Sheet, Spinner, useLoad } from "./bits";
import { CharactersEditor, newCharacter } from "./BriefFields";
import { useModels } from "./ModelPicker";
import { active } from "./ShotBoard";
import { OlderBadge } from "./OlderLook";
import { VoiceSearch } from "./VoiceSearch";

// ------------------------------------------------------------------ tiles (collections)

/** A cast member's small card: its sheet, name, state and version, and older shots (never folded). */
export function CastTile({ m, href, focused = false }: { m: CastMember; href: string; focused?: boolean }) {
  const v = m.state === "draft" || m.state === "making" ? m.latestN : (m.approvedN ?? m.latestN);
  const jobs = useSheetJobs(m.id);
  const making = jobs.length > 0 || m.state === "making";
  return (
    <Link to={href} className={`cast-tile cast-${making ? "making" : m.state}${focused ? " is-focused" : ""}`} aria-label={`${m.name}, ${STATE_WORD[making ? "making" : m.state]}`}>
      <span className="cast-tile-art">
        {m.thumbUrl ? <img src={m.thumbUrl} alt="" loading="lazy" /> : <span className="cast-tile-mono">{m.name.slice(0, 1)}</span>}
        {making && <span className="cast-tile-pct">{jobs.length ? `${Math.round(sheetProgress(jobs) * 100)}%` : "…"}</span>}
      </span>
      <span className="cast-tile-name">{m.name}</span>
      <span className="cast-tile-state">
        {m.state === "approved" && !making ? <Icon name="check" size={13} /> : <span className="cast-tile-dot" aria-hidden />}
        {v ? `v${v} · ` : ""}
        {STATE_WORD[making ? "making" : m.state]}
      </span>
      <OlderBadge count={m.olderShots} className="cast-tile-older" />
    </Link>
  );
}

/** A row of tiles. On phones it scrolls sideways and keeps the page's gutter on both ends. */
export function CastRow({ members, hrefOf, focus }: { members: CastMember[]; hrefOf: (m: CastMember) => string; focus?: string }) {
  return (
    <ul className="cast-row">
      {members.map((m) => (
        <li key={m.id} id={`cast-${m.id}`}>
          <CastTile m={m} href={hrefOf(m)} focused={m.id === focus || m.slug === focus} />
        </li>
      ))}
    </ul>
  );
}

// ------------------------------------------------------------------ a video's cast

export function castOf(p: Project): Character[] {
  return videoBrief(p).characters ?? p.script?.characters ?? [];
}

/** The video's characters as rows (GET /projects/:id/characters), by script id. Empty on an older API. */
export function useCastRows(p: Pick<Project, "id">): { rows: Map<string, CharacterInfo> } {
  const tick = useRefresh("characters", "project");
  const { data } = useLoad<CharacterInfo[] | null>(() => characters.ofProject(p.id).catch(() => null), [p.id, tick]);
  return { rows: new Map((data ?? []).map((c) => [c.slug.toLowerCase(), c])) };
}

/** A character's sheet jobs that are still running (sheet jobs have no project: they're followed by character). */
export function useSheetJobs(characterId: string | null | undefined): JobView[] {
  const all = useStore((s) => s.jobs);
  return characterId ? sheetJobs(all, characterId) : [];
}

/** Starts a sheet (or a part of one) and tells the Director. Returns false when it couldn't start. */
export async function startSheet(c: Pick<CharacterInfo, "id" | "name">, body: SheetBody, credits: number | null): Promise<boolean> {
  try {
    const r = await characters.makeSheet(c.id, body);
    r.jobs.forEach(onJob);
    bump("credits", "characters", "project", "collections");
    const what = !body.part || body.part === "all" ? "sheet" : `${body.part} part of the sheet`;
    director.context(`The user started ${c.name}'s ${what} by tapping (${r.credits || credits || "a few"} credits${body.note ? `, asking: "${body.note}"` : ""}). Don't start it again; it's ready for a look when it's drawn.`);
    if (r.said) toast(r.said, "good");
    return true;
  } catch (e) {
    toast(`Couldn't start the sheet. ${(e as Error).message}`, "bad");
    return false;
  }
}

export function CastPanel({ p, format, onProject }: { p: Project; format: Pick<FormatInfo, "voices">; onProject: (p: Project) => void }) {
  const cast = castOf(p);
  const models = useModels();
  const image = models?.find((m) => m.id === p.models.image) ?? models?.find((m) => m.step === "image" && m.isDefault);
  const { rows } = useCastRows(p);
  const [adding, setAdding] = useState(false);
  const hosts = format.voices === "hosts";
  // v4: a sheet is three pictures (spec 6.4): three times the image model's frame price, 45 at the default
  const v3Price = image?.credits != null ? image.credits * 3 : SHEET_CREDITS;
  return (
    <section className="vpanel cast" aria-labelledby="cast-h">
      <header className="vpanel-head">
        <h2 id="cast-h">{hosts ? "Hosts" : "Cast"}</h2>
        {cast.length < (hosts ? 2 : 6) && (
          <button data-action="cast.add" type="button" className="btn btn-quiet btn-small" onClick={() => setAdding(true)}>
            <Icon name="plus" size={14} /> {hosts ? "Add a host" : "Add a character"}
          </button>
        )}
      </header>
      {!cast.length ? (
        <p className="muted">{p.script ? "This script has no characters yet. Add one, or ask your Director to bring someone in." : "The script invents the characters, or add your own first. Each one keeps the same look and voice in every scene."}</p>
      ) : (
        <ul className="cast-list">
          {cast.map((c) => (
            <li key={c.id}>
              <CastCard p={p} c={c} row={rows.get(c.id.toLowerCase()) ?? null} sheetCredits={v3Price} onProject={onProject} />
            </li>
          ))}
        </ul>
      )}
      {adding && <AddCharacter p={p} hosts={hosts} onClose={() => setAdding(false)} onProject={onProject} />}
    </section>
  );
}

function CastCard({ p, c, row, sheetCredits, onProject }: { p: Project; c: Character; row: CharacterInfo | null; sheetCredits: number; onProject: (p: Project) => void }) {
  // v3 fallback: the project route's job, followed by its id
  const [v3JobId, setV3JobId] = useState<string | null>(null);
  const v3Job = useStore((s) => (v3JobId ? s.jobs[v3JobId] : undefined));
  const jobs = useSheetJobs(row?.id);
  const [confirm, setConfirm] = useState(false);
  const [edit, setEdit] = useState(false);
  const [voice, setVoice] = useState(false);
  const working = jobs.length > 0 || active(v3Job) || row?.state === "making";
  const pct = jobs.length ? sheetProgress(jobs) : (v3Job?.progress ?? 0);
  useEffect(() => {
    if (v3Job?.status === "done") {
      bump("project");
      setV3JobId(null);
    } else if (v3Job?.status === "failed") toast(v3Job.error ?? `Couldn't make ${c.name}'s sheet.`, "bad");
  }, [v3Job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = row ? (row.state === "draft" || row.state === "making" ? row.latest : (row.approved ?? row.latest)) : null;
  const thumb = versionThumb(shown) ?? c.refUrl ?? null;
  const hasSheet = !!(row ? row.approved || row.latest : c.refUrl);
  const href = characterPath({ projectId: p.id }, c.id);
  const state = working ? "making" : (row?.state ?? (c.refUrl ? "approved" : "none"));
  const vn = shown?.n ?? null;

  const make = async () => {
    setConfirm(false);
    if (row) {
      await startSheet(row, { part: "all" }, sheetCredits);
      return;
    }
    try {
      const j = await api.makeSheet(p.id, c.id);
      onJob(j);
      setV3JobId(j.id);
      bump("credits");
      director.context(`The user started ${c.name}'s character sheet by tapping (job ${j.id}, about ${j.credits} credits). Don't start it again.`);
    } catch (e) {
      toast(`Couldn't start the sheet. ${(e as Error).message}`, "bad");
    }
  };

  return (
    <article className={`ccard ccard-${state}`} aria-label={c.name}>
      <Link to={href} className="ccard-sheet" aria-label={`Open ${c.name}'s sheet`}>
        {thumb ? <img src={thumb} alt="" loading="lazy" /> : <span className="ccard-initial" aria-hidden>{c.name.slice(0, 1).toUpperCase()}</span>}
        {working && (
          <span className="ccard-working">
            <span className="frame-scan" aria-hidden />
            <span className="ccard-pct">{Math.round(pct * 100)}%</span>
          </span>
        )}
      </Link>
      <div className="ccard-body">
        <header className="ccard-head">
          <h3>
            <Link to={href} className="ccard-name">
              {c.name}
            </Link>
          </h3>
          <button data-action="cast.edit" type="button" className="icon-btn" aria-label={`Edit ${c.name}`} onClick={() => setEdit(true)}>
            <Icon name="edit" size={16} />
          </button>
        </header>
        <p className={`ccard-state ccard-state-${state}`}>
          {state === "approved" ? <Icon name="check" size={13} /> : <span className="cast-tile-dot" aria-hidden />}
          {vn ? `v${vn} · ` : ""}
          {STATE_WORD[state]}
          {row?.lookChanged && state === "approved" && <span className="muted"> · description changed</span>}
        </p>
        <p className="ccard-look">{c.look || "No description yet. Tap edit to say what they look like."}</p>
        {row && row.olderShots > 0 && (
          <Link to={href} className="ccard-older">
            <OlderBadge count={row.olderShots} label={`${row.olderShots} shot${row.olderShots === 1 ? "" : "s"} use an older look`} />
          </Link>
        )}
        <div className="ccard-voice">
          <Icon name="mic" size={15} />
          <span>
            <strong>{c.voiceName ?? (c.voiceId ? "Cast" : "Not cast yet")}</strong>
            <span className="muted">{c.voice || "Cast from how they're described"}</span>
          </span>
          <button data-action="cast.voice" type="button" className="btn btn-quiet btn-small" onClick={() => setVoice(true)}>
            {c.voiceId ? "Change" : "Pick a voice"}
          </button>
        </div>
        {!hasSheet && !working && (
          <button data-action="character.sheet" type="button" className="btn btn-quiet btn-small ccard-make" onClick={() => setConfirm(true)}>
            <Icon name="image" size={15} /> Make the character sheet<span className="ccard-price">{fmtCredits(sheetCredits)}</span>
          </button>
        )}
        {state === "draft" && (
          <Link to={href} className="btn btn-primary btn-small ccard-make">
            Look at v{vn} and approve it
          </Link>
        )}
        {hasSheet && state !== "draft" && !working && (
          <Link to={href} className="ccard-redo">
            <Icon name="image" size={14} /> {row ? "Sheet, versions and redo" : "Open the sheet"}
          </Link>
        )}
      </div>
      {confirm && <SheetConfirm name={c.name} what="all" credits={sheetCredits} hasSheet={false} onClose={() => setConfirm(false)} onConfirm={() => void make()} />}
      {edit && <EditCharacter p={p} c={c} row={row} sheetCredits={sheetCredits} onClose={() => setEdit(false)} onProject={onProject} />}
      {voice && (
        <VoicePick
          who={{ key: c.id, name: c.name, look: c.look, voice: c.voice, voiceId: c.voiceId ?? null }}
          taken={new Set(castOf(p).filter((x) => x.id !== c.id && x.voiceId).map((x) => x.voiceId!))}
          onClose={() => setVoice(false)}
          onPick={(v) => patchChar(p, c, { voiceId: v.voiceId }, `cast ${v.name} as ${c.name}'s voice`, onProject)}
        />
      )}
    </article>
  );
}

// ------------------------------------------------------------------ the priced sheet confirm

/**
 * "Make Pip's sheet?" / "Redo just the faces?": the price first, an optional note on what to change, then one button
 * with the price on it.
 */
export function SheetConfirm({ name, what, credits, hasSheet, onClose, onConfirm, askNote = false, lead }: { name: string; what: "all" | SheetPart; credits: number | null; hasSheet: boolean; onClose: () => void; onConfirm: (note: string) => void; askNote?: boolean; lead?: string }) {
  const [note, setNote] = useState("");
  const title = what === "all" ? (hasSheet ? `Make a new sheet of ${name}?` : `Make ${name}'s sheet?`) : `${sheetButton(what, null, true)} of ${name}?`;
  const body =
    lead ??
    (what === "all"
      ? `Three pictures: four angles, six faces and four poses. Every scene ${name} is in is drawn from it, so they look the same throughout.${hasSheet ? " It becomes a new version; the approved one stays until you approve this." : ""}`
      : `The other parts stay as they are. It becomes a new version for you to check.`);
  return (
    <Sheet title={title} onClose={onClose}>
      <form
        className="sheet-body confirm"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          onConfirm(note.trim());
        }}
      >
        <p className="confirm-num">
          {credits == null ? "…" : fmtCredits(credits)} <span>credits</span>
        </p>
        <p>{body}</p>
        {askNote && (
          <label className="field">
            <span className="field-label">What should change? (optional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="A longer scarf, rounder ears" />
          </label>
        )}
        <div className="confirm-actions">
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Not yet
          </button>
          <button type="submit" className="btn btn-primary" data-autofocus>
            {what === "all" ? (hasSheet ? "Make it" : "Make it") : "Redo it"}
            {credits != null ? ` · ${fmtCredits(credits)}` : ""}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

// ------------------------------------------------------------------ edits

async function patchChar(p: Project, c: Character, patch: Partial<Character>, said: string, onProject: (p: Project) => void) {
  try {
    const next = await api.patchCharacter(p.id, c.id, { name: patch.name, look: patch.look, voice: patch.voice, voiceId: patch.voiceId });
    onProject(next);
    bump("characters");
    director.context(`The user ${said} on "${p.title}" by tapping.`);
    return true;
  } catch (e) {
    toast(`Couldn't save ${c.name}. ${(e as Error).message}`, "bad");
    return false;
  }
}

function EditCharacter({ p, c, row, sheetCredits, onClose, onProject }: { p: Project; c: Character; row: CharacterInfo | null; sheetCredits: number; onClose: () => void; onProject: (p: Project) => void }) {
  const [name, setName] = useState(c.name);
  const [look, setLook] = useState(c.look);
  const [voice, setVoice] = useState(c.voice);
  const [busy, setBusy] = useState(false);
  const [askNew, setAskNew] = useState(false);
  const dirty = name !== c.name || look !== c.look || voice !== c.voice;
  if (askNew && row)
    return (
      <SheetConfirm
        name={c.name}
        what="all"
        credits={sheetCredits}
        hasSheet
        lead={`${c.name}'s description changed. Make a new sheet so the pictures match? The approved sheet stays until you approve the new one.`}
        onClose={onClose}
        onConfirm={(note) => {
          onClose();
          void startSheet(row, { part: "all", ...(note ? { note } : {}) }, sheetCredits);
        }}
      />
    );
  return (
    <Sheet title={c.name} onClose={onClose}>
      <form
        className="sheet-body"
        data-unsaved={dirty ? "true" : undefined}
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          const lookChanged = look.trim() !== c.look.trim();
          if (await patchChar(p, c, { name: name.trim() || c.name, look: look.trim(), voice: voice.trim() }, `edited ${c.name}`, onProject)) {
            if (lookChanged && row && (row.approved || row.latest)) setAskNew(true);
            else onClose();
          }
          setBusy(false);
        }}
      >
        <label className="field">
          <span className="field-label">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
        </label>
        <label className="field">
          <span className="field-label">What they look like</span>
          <textarea value={look} onChange={(e) => setLook(e.target.value)} rows={3} maxLength={600} placeholder="A small red fox with a striped scarf and big, worried eyes" />
          <span className="field-hint">The sheet and every frame they're in follow this. The current sheet stays until you make a new one.</span>
        </label>
        <label className="field">
          <span className="field-label">How they sound</span>
          <input value={voice} onChange={(e) => setVoice(e.target.value)} maxLength={200} placeholder="Bright, a little nervous, young" />
        </label>
        <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
          {busy ? "Saving" : "Save"}
        </button>
      </form>
    </Sheet>
  );
}

/** Words a voice is described with, matched against what the user typed (no request, no cost). */
function score(v: VoiceInfo, words: string[]) {
  const hay = `${v.name} ${v.description} ${v.tags.join(" ")} ${v.gender ?? ""} ${v.age ?? ""} ${v.accent ?? ""}`.toLowerCase();
  return words.reduce((a, w) => a + (hay.includes(w) ? 1 : 0), 0);
}

export type VoiceFor = { key: string; name: string; look: string; voice: string; voiceId: string | null };

/** Pick a voice by describing it; play one reads the character's line. `onPick` saves it (true closes the sheet). */
export function VoicePick({ who, taken, onClose, onPick }: { who: VoiceFor; taken: Set<string>; onClose: () => void; onPick: (v: VoiceInfo) => Promise<boolean> }) {
  const { data: voices, loading } = useLoad<VoiceInfo[]>(() => api.voices().catch(() => []), []);
  const [q, setQ] = useState(who.voice);
  const [busy, setBusy] = useState(false);
  const playing = useStore((s) => s.playing);
  const [sampling, setSampling] = useState<string | null>(null);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const words = q.toLowerCase().split(/[^a-z]+/).filter((w) => w.length > 2);
  const sorted = [...(voices ?? [])].sort((a, b) => score(b, words) - score(a, words));
  const line = who.look ? `Hi. I'm ${who.name}.` : `I'm ${who.name}. Nice to meet you.`;
  const hear = async (v: VoiceInfo) => {
    const key = `cv-${who.key}-${v.voiceId}`;
    if (urls[v.voiceId]) return toggleAudio(key, urls[v.voiceId]);
    setSampling(v.voiceId);
    const r = await tapTool("voice_sample", { voice_id: v.voiceId, text: line }, `auditioned ${v.name} for ${who.name}`, { keepCards: true });
    setSampling(null);
    const url = r?.cards[0]?.url;
    if (url) {
      setUrls((u) => ({ ...u, [v.voiceId]: url }));
      toggleAudio(key, url);
    }
  };
  return (
    <Sheet title={`${who.name}'s voice`} onClose={onClose}>
      <div className="sheet-body">
        <label className="field">
          <span className="field-label">Describe how {who.name} sounds</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Older, warm, British" />
          <span className="field-hint">The closest voices come first. Tap play to hear one read {who.name}'s line.</span>
        </label>
        {loading && !voices && <Spinner label="Loading voices" />}
        <ul className="vrows vrows-pick" role="radiogroup">
          {sorted.map((v) => {
            const on = v.voiceId === who.voiceId;
            const key = `cv-${who.key}-${v.voiceId}`;
            return (
              <li key={v.voiceId} className={`vrow${on ? " is-on" : ""}`}>
                <button data-action="cast.hear" type="button" className={`card-audio-btn${playing === key ? " is-on" : ""}`} onClick={() => void hear(v)} disabled={sampling === v.voiceId} aria-label={`Hear ${v.name}`}>
                  <Icon name={playing === key ? "pause" : sampling === v.voiceId ? "wave" : "play"} size={16} />
                </button>
                <span className="vrow-text">
                  <strong>
                    {v.name}
                    {taken.has(v.voiceId) && <span className="tag">In the cast</span>}
                  </strong>
                  <span className="muted">{v.description}</span>
                </span>
                <button
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className={on ? "btn btn-primary btn-small" : "btn btn-quiet btn-small"}
                  disabled={busy || on || taken.has(v.voiceId)}
                  onClick={async () => {
                    setBusy(true);
                    if (await onPick(v)) {
                      toast(`${v.name} is ${who.name} now.`, "good");
                      onClose();
                    }
                    setBusy(false);
                  }}
                >
                  {on ? "Cast" : "Use"}
                </button>
              </li>
            );
          })}
        </ul>
        <VoiceSearch
          for="character"
          initial={who.voice}
          onUse={async (v) => {
            if (taken.has(v.voiceId)) return toast("Another character already has that voice.", "bad");
            setBusy(true);
            if (await onPick({ voiceId: v.voiceId, name: v.name, description: v.description, tags: [] })) {
              toast(`${v.name} is ${who.name} now.`, "good");
              onClose();
            }
            setBusy(false);
          }}
        />
      </div>
    </Sheet>
  );
}

export function AddCharacter({ p, hosts, onClose, onProject }: { p: Project; hosts: boolean; onClose: () => void; onProject: (p: Project) => void }) {
  const cast = castOf(p);
  const [list, setList] = useState<Character[]>(() => [newCharacter(cast)]);
  const [busy, setBusy] = useState(false);
  return (
    <Sheet title={hosts ? "Add a host" : "Add a character"} onClose={onClose}>
      <form
        className="sheet-body"
        onSubmit={async (e) => {
          e.preventDefault();
          const add = list.filter((c) => c.name.trim());
          if (!add.length) return;
          setBusy(true);
          try {
            const next = await api.patchProject(p.id, { brief: { ...videoBrief(p), characters: [...cast, ...add] } });
            onProject(next);
            bump("characters");
            director.context(`The user added ${add.map((c) => c.name).join(" and ")} to the cast of "${p.title}" by tapping.`);
            onClose();
          } catch (err) {
            toast(`Couldn't add them. ${(err as Error).message}`, "bad");
          } finally {
            setBusy(false);
          }
        }}
      >
        <CharactersEditor value={list} onChange={setList} max={6 - cast.length} hosts={hosts} />
        <button type="submit" className="btn btn-primary btn-block" disabled={busy || !list.some((c) => c.name.trim())}>
          {busy ? "Adding" : "Add to the cast"}
        </button>
      </form>
    </Sheet>
  );
}
